import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { IoArrowForward, IoClose } from 'react-icons/io5';
import { RoleDefinition } from 'src/types/permissions.types';
import { getShellLabel } from 'src/config/permissionCatalog';
import { RoleMember } from 'src/services/role.service';
import { goldButtonSx, personName, roleAccent } from './RoleUi';

interface AssignRoleModalProps {
  open: boolean;
  onClose: () => void;
  members: RoleMember[];
  roles: RoleDefinition[];
  /** Pre-selected person, when opened from a member row. */
  initialMember?: RoleMember | null;
  /** Pre-selected role, when opened from a role workspace. */
  initialRole?: string;
  onSubmit: (userId: string, roleName: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

export function AssignRoleModal({
  open,
  onClose,
  members,
  roles,
  initialMember,
  initialRole,
  onSubmit,
  isSubmitting,
}: AssignRoleModalProps) {
  const [member, setMember] = useState<RoleMember | null>(null);
  const [roleName, setRoleName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMember(initialMember || null);
    setRoleName(initialRole || '');
    setError(null);
  }, [open, initialMember, initialRole]);

  const labelOf = (name?: string | null) => roles.find((r) => r.name === name)?.label || name || 'No role';
  const unchanged = Boolean(member && member.role === roleName);

  const handleSubmit = async () => {
    if (!member || !roleName) return;
    try {
      setError(null);
      await onSubmit(member.id, roleName);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to change role.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1A202C">
            Assign Role
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Choose a person and the role they should have. It applies at their next page load.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {error && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FFF5F5', color: '#E53E3E', borderRadius: 1, fontSize: '0.875rem' }}>{error}</Box>
        )}

        <Stack spacing={2.5}>
          <Autocomplete
            options={members}
            value={member}
            onChange={(_, value) => setMember(value)}
            getOptionLabel={(m) => `${personName(m)} (${m.email})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, m) => (
              <Box component="li" {...props} key={m.id} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}>
                  {(m.firstname || m.email)[0]?.toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {personName(m)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {m.email}
                  </Typography>
                </Box>
                <Chip label={labelOf(m.role)} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#F1F5F9' }} />
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Person" placeholder="Search by name or email" size="small" />}
          />

          <Box>
            <Typography variant="caption" fontWeight={700} color="#64748B" letterSpacing={0.5} sx={{ display: 'block', mb: 1 }}>
              ROLE
            </Typography>
            <Stack spacing={0.75} role="radiogroup" aria-label="Role">
              {roles.map((role) => {
                const selected = role.name === roleName;
                const { color } = roleAccent(role);
                return (
                  <Box
                    key={role.name}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => setRoleName(role.name)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setRoleName(role.name);
                      }
                    }}
                    sx={{
                      px: 1.25,
                      py: 1,
                      borderRadius: 1.75,
                      border: '1px solid',
                      borderColor: selected ? color : '#E2E8F0',
                      bgcolor: selected ? alpha(color, 0.06) : '#FFFFFF',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      '&:hover': { borderColor: color },
                      '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} fontSize="0.8125rem" color="#0F172A">
                        {role.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize="0.7rem" noWrap display="block">
                        {getShellLabel(role.base_route)}
                        {role.description ? ` · ${role.description}` : ''}
                      </Typography>
                    </Box>
                    {selected && <Icon icon="solar:check-circle-bold" width={18} color={color} />}
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {roleName === 'BANKER' && (
            <Alert severity="info" icon={<Icon icon="solar:wallet-money-bold-duotone" width={20} />} sx={{ borderRadius: 2 }}>
              A Student Banker must be a scholar. They get teacher tools for the cohorts they are enrolled in, and never for their
              own account.
            </Alert>
          )}

          {member && roleName && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}
            >
              <Typography variant="body2" fontWeight={600} color="#0F172A" sx={{ flex: 1 }} noWrap>
                {personName(member)}
              </Typography>
              <Chip label={labelOf(member.role)} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#E2E8F0' }} />
              <IoArrowForward size={14} color="#64748B" />
              <Chip
                label={labelOf(roleName)}
                size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha('#EDB42E', 0.2), color: '#854D0E' }}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!member || !roleName || unchanged || isSubmitting}
          onClick={handleSubmit}
          sx={goldButtonSx}
        >
          {isSubmitting ? 'Saving...' : unchanged ? 'Already has this role' : 'Assign Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
